// Query Intent Validator — gates nonsense queries before recommendation engine

export type QueryIntent = "valid" | "ambiguous" | "rejected";

export interface IntentResult {
  intent: QueryIntent;
  reason: string;
  signals: string[];
  suggestedClarifications?: string[];
}

// ─── Rejection patterns ──────────────────────────────────────────────────────

const GREETING_PATTERNS = /^(hi|hello|hey|yo|sup|what'?s\s*up|how\s*are\s*you|good\s*(morning|evening|afternoon)|howdy|greetings)\b/i;
const MIC_TEST_PATTERNS = /\b(can\s*you\s*hear\s*me|is\s*this\s*(working|on)|test(ing)?|check\s*check|one\s*two\s*three|hello\s*hello|mic\s*check)\b/i;
const CONVERSATIONAL_PATTERNS = /^(yes|no|ok(ay)?|sure|thanks|thank\s*you|please|sorry|bye|goodbye|see\s*you|what|who|when|where|why|how)\s*[.!?]?$/i;
const GIBBERISH_PATTERN = /^[^a-zA-Z]*$|^(.)\1{4,}/;
const QUESTION_ONLY = /^(what|who|when|where|why|how)\s+(is|are|was|were|do|does|did|can|could|should|would|will)\s/i;

// ─── Valid shopping signals ──────────────────────────────────────────────────

const BUDGET_PATTERN = /(?:under|below|less\s*than|up\s*to|around|about|max|budget|~)\s*[\$€£¥₹]?\s*\d+|[\$€£¥₹]\s*\d+|\d+\s*(?:dollars|euros|pounds|usd|eur|gbp|cad|aud|aed|ish|or\s*less)/i;
const COMPARISON_KEYWORDS = /\b(best|top|vs\.?|versus|better\s*than|compared?\s*to|recommend|review|rating|ranked|which|should\s*i\s*(buy|get)|worth\s*(it|buying|getting))\b/i;
const PURCHASE_INTENT = /\b(buy|purchase|order|get|need|looking\s*for|want|shopping\s*for|searching\s*for|in\s*the\s*market|considering|thinking\s*of\s*(buying|getting))\b/i;
const DELIVERY_SIGNALS = /\b(deliver(y|ed)?|ship(ping|ped)?|tomorrow|next\s*day|fast\s*delivery|free\s*shipping|prime|express)\b/i;

// Product categories (single words that are valid product types)
const PRODUCT_KEYWORDS = new Set([
  "monitor", "monitors", "laptop", "laptops", "keyboard", "keyboards", "mouse", "mice",
  "tablet", "tablets", "phone", "phones", "smartphone", "tv", "television", "speaker", "speakers",
  "camera", "cameras", "webcam", "headphone", "headphones", "earbuds", "earphone", "earphones",
  "vacuum", "vacuums", "blender", "blenders", "microwave", "toaster", "dishwasher", "dryer",
  "refrigerator", "fridge", "mattress", "pillow", "treadmill", "smartwatch",
  "chair", "desk", "table", "shelf", "shelves", "bookcase", "sofa", "couch",
  "printer", "scanner", "router", "modem", "projector", "drone",
  "backpack", "luggage", "suitcase", "wallet", "watch", "sunglasses",
  "shoes", "boots", "sneakers", "jacket", "coat",
  "stroller", "car seat", "crib", "highchair",
  "grill", "smoker", "oven", "cooktop", "range",
  // Common typos for wedge categories
  "moniter", "monior", "monitr", "headphons", "hedphones",
  "vacum", "vaccum", "vacume", "expresso", "keybord",
  "laptp", "protien", "tredmill", "smartwach",
]);

const COMPOUND_PRODUCT_KEYWORDS = [
  "office chair", "desk chair", "gaming chair", "standing desk",
  "air fryer", "air purifier", "air conditioner",
  "coffee maker", "espresso machine", "coffee grinder",
  "robot vacuum", "vacuum cleaner", "washing machine",
  "protein powder", "whey protein", "protein shake",
  "skincare device", "fitness tracker", "exercise bike", "yoga mat",
  "gaming mouse", "gaming keyboard", "gaming headset", "gaming monitor",
  "noise cancelling", "wireless earbuds", "bluetooth speaker",
  "baby monitor", "car seat", "high chair",
  "pressure washer", "lawn mower", "leaf blower",
  "instant pot", "slow cooker", "rice cooker", "food processor",
  "hair dryer", "curling iron", "flat iron", "electric razor", "electric toothbrush",
];

// Brand names that signal product intent
const BRAND_NAMES = new Set([
  "samsung", "lg", "dell", "asus", "apple", "sony", "bose", "dyson", "shark",
  "ninja", "breville", "jura", "herman miller", "steelcase", "secretlab",
  "roborock", "miele", "bissell", "cosori", "instant", "kitchenaid",
  "logitech", "razer", "corsair", "hyperx", "sennheiser", "jabra",
  "nike", "adidas", "new balance", "under armour",
  "philips", "braun", "panasonic", "bosch", "whirlpool", "maytag", "ge",
  "lenovo", "hp", "acer", "msi", "gigabyte", "benq",
  "nuface", "foreo", "theragun", "peloton", "garmin", "fitbit",
  "optimum nutrition", "myprotein", "transparent labs",
  "roomba", "irobot", "ecovacs", "tineco",
  "nespresso", "keurig", "cuisinart", "oxo", "vitamix",
]);

// ─── Filler word stripping (voice transcript cleanup) ────────────────────────

const FILLER_PATTERN = /\b(um+|uh+|ah+|er+|hmm+|like|you know|i mean|so+|well|okay so|right so|actually)\b/gi;

export function sanitizeVoiceTranscript(raw: string): string {
  return raw
    .replace(FILLER_PATTERN, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

// ─── Core validator ──────────────────────────────────────────────────────────

export function validateQueryIntent(raw: string): IntentResult {
  const q = raw.trim();
  const lower = q.toLowerCase();
  const words = lower.split(/\s+/).filter(Boolean);

  // Empty
  if (!q || words.length === 0) {
    return { intent: "rejected", reason: "empty_input", signals: [] };
  }

  // Gibberish
  if (GIBBERISH_PATTERN.test(q)) {
    return { intent: "rejected", reason: "gibberish", signals: [] };
  }

  // Greetings
  if (GREETING_PATTERNS.test(lower)) {
    // Could be "hi, best headphones?" — check rest for product signal
    const withoutGreeting = lower.replace(GREETING_PATTERNS, "").trim();
    if (withoutGreeting.length < 3 || !hasProductSignal(withoutGreeting)) {
      return { intent: "rejected", reason: "greeting", signals: ["greeting_detected"] };
    }
  }

  // Mic tests
  if (MIC_TEST_PATTERNS.test(lower)) {
    return { intent: "rejected", reason: "mic_test", signals: ["mic_test_detected"] };
  }

  // Pure conversational (short responses with no product context)
  if (CONVERSATIONAL_PATTERNS.test(lower) && words.length <= 3) {
    return { intent: "rejected", reason: "conversational", signals: ["conversational_detected"] };
  }

  // General question with no product signal
  if (QUESTION_ONLY.test(lower) && !hasProductSignal(lower)) {
    return { intent: "rejected", reason: "non_product_question", signals: ["question_without_product"] };
  }

  // Very short with no signal
  if (words.length <= 2 && !hasProductSignal(lower)) {
    return { intent: "rejected", reason: "too_short_no_signal", signals: [] };
  }

  // ─── Check for valid signals ──────────────────────────────────────────────
  const signals = collectSignals(lower);

  // Strong intent: 2+ signals or comparison/budget present
  if (signals.length >= 2) {
    return { intent: "valid", reason: "strong_product_intent", signals };
  }

  // Single product keyword with nothing else → ambiguous
  if (signals.length === 1 && signals[0].startsWith("product_keyword")) {
    return {
      intent: "ambiguous",
      reason: "vague_product_query",
      signals,
      suggestedClarifications: [
        "What's your budget?",
        "What will you use it for?",
        "What country are you in?",
        "What matters most: price, quality, or fast delivery?",
      ],
    };
  }

  // Single signal (brand, budget, etc.) → valid enough
  if (signals.length === 1) {
    return { intent: "valid", reason: "single_strong_signal", signals };
  }

  // No signals at all, but long enough query — might be a use-case description
  if (words.length >= 5) {
    // Last chance: check semantic triggers
    if (hasSemanticIntent(lower)) {
      return { intent: "valid", reason: "semantic_use_case", signals: ["semantic_match"] };
    }
    return { intent: "rejected", reason: "no_product_intent", signals: [] };
  }

  return { intent: "rejected", reason: "no_product_intent", signals: [] };
}

// ─── Signal collection ───────────────────────────────────────────────────────

function collectSignals(lower: string): string[] {
  const signals: string[] = [];

  // Check compound keywords first
  for (const compound of COMPOUND_PRODUCT_KEYWORDS) {
    if (lower.includes(compound)) {
      signals.push(`product_keyword:${compound}`);
      break; // one product match is enough
    }
  }

  // Check single product keywords
  if (signals.length === 0) {
    const words = lower.split(/\s+/);
    for (const word of words) {
      if (PRODUCT_KEYWORDS.has(word)) {
        signals.push(`product_keyword:${word}`);
        break;
      }
    }
  }

  // Check brands
  for (const brand of BRAND_NAMES) {
    if (lower.includes(brand)) {
      signals.push(`brand:${brand}`);
      break;
    }
  }

  // Budget
  if (BUDGET_PATTERN.test(lower)) signals.push("budget");

  // Comparison / recommendation intent
  if (COMPARISON_KEYWORDS.test(lower)) signals.push("comparison_intent");

  // Purchase intent
  if (PURCHASE_INTENT.test(lower)) signals.push("purchase_intent");

  // Delivery signals
  if (DELIVERY_SIGNALS.test(lower)) signals.push("delivery_signal");

  return signals;
}

function hasProductSignal(lower: string): boolean {
  return collectSignals(lower).length > 0;
}

function hasSemanticIntent(lower: string): boolean {
  const USE_CASE_TRIGGERS = [
    "back pain", "posture", "sitting all day", "color accurate", "photo editing",
    "video editing", "gaming", "pet hair", "dog hair", "cat hair", "quiet apartment",
    "real espresso", "barista", "block noise", "airplane", "commute", "workout",
    "muscle building", "post workout", "healthy cooking", "oil free", "anti aging",
    "wrinkle", "acne", "clear skin", "meal prep", "home office", "streaming",
    "music production", "podcasting", "recording",
  ];
  return USE_CASE_TRIGGERS.some(t => lower.includes(t));
}
